// Live's built-in devices as they come out of the browser: their parameters
// at the values a fresh instance has, by the names Live's API reports.
//
// Two uses. A room with no Live Set in it (a browser room, or a jam no
// extension has joined) has nobody to fill in an inserted device, so "+
// device" inserts it filled in from here — editable and heard at once. And
// a placeholder waiting for a Set to fill it in is heard with these values
// until it does (device-graph.ts), so an insert is never silent.
//
// The values only need to sound right in the browser's stand-in; once a Set
// fills a device in, Live's own parameters replace them.

import type { DeviceParam, DeviceState } from "@shared/sync/state.js";

const on = (name: string, value: boolean): DeviceParam => ({ name, value: value ? 1 : 0, min: 0, max: 1, quantized: true });
const knob = (name: string, value: number, min: number, max: number): DeviceParam => ({ name, value, min, max });
const pick = (name: string, value: number, items: string[]): DeviceParam => ({ name, value, min: 0, max: items.length - 1, quantized: true, items });

const EQ_TYPES = ["Low Cut 48", "Low Cut 12", "Low Shelf", "Bell", "Notch", "High Shelf", "High Cut 12", "High Cut 48"];
const eqPos = (hz: number): number => Math.log(hz / 10) / Math.log(22000 / 10);
const SIXTEENTHS = ["1", "2", "3", "4", "5", "6", "8", "16"];
const RATES = ["8", "4", "2", "1", "1/2", "1/3", "1/4", "1/6", "1/8", "1/12", "1/16", "1/24", "1/32"];

function eqEight(): DeviceParam[] {
  const bands: Array<[boolean, number, number]> = [
    [false, 1, 40],
    [true, 2, 100],
    [true, 3, 250],
    [true, 3, 1000],
    [true, 3, 2500],
    [true, 3, 5000],
    [true, 5, 8000],
    [false, 7, 15000],
  ];
  return [
    on("Device On", true),
    ...bands.flatMap(([enabled, type, hz], i) => [
      on(`${i + 1} Filter On A`, enabled),
      pick(`${i + 1} Filter Type A`, type, EQ_TYPES),
      knob(`${i + 1} Frequency A`, eqPos(hz), 0, 1),
      knob(`${i + 1} Gain A`, 0, -15, 15),
      knob(`${i + 1} Resonance A`, 0.71, 0.1, 18),
    ]),
    knob("Scale", 1, -2, 2),
    knob("Output Gain", 0, -12, 12),
  ];
}

const STOCK: Record<string, () => DeviceParam[]> = {
  "EQ Eight": eqEight,
  Delay: () => [
    on("Device On", true),
    on("L Sync", true),
    pick("L 16th", 2, SIXTEENTHS),
    knob("L Time", 250, 1, 5000),
    knob("L Offset", 0, -0.333, 0.333),
    on("R Sync", true),
    pick("R 16th", 3, SIXTEENTHS),
    knob("R Time", 375, 1, 5000),
    knob("R Offset", 0, -0.333, 0.333),
    on("Link", true),
    on("Ping Pong", false),
    knob("Feedback", 0.5, 0, 0.95),
    on("Freeze", false),
    on("Filter On", true),
    knob("Filter Freq", 1000, 50, 18000),
    knob("Filter Width", 8, 0.5, 9),
    knob("Dry/Wet", 0.5, 0, 1),
  ],
  Echo: () => [
    on("Device On", true),
    on("L Sync", true),
    pick("L 16th", 3, SIXTEENTHS),
    knob("L Time", 375, 1, 5000),
    on("Link", true),
    knob("Feedback", 0.5, 0, 1),
    knob("Dry Wet", 0.4, 0, 1),
  ],
  Reverb: () => [
    on("Device On", true),
    knob("PreDelay", 2.5, 0.5, 250),
    on("In LowCut On", true),
    on("In HighCut On", true),
    knob("In Filter Freq", 830, 50, 18000),
    knob("In Filter Width", 7.5, 0.5, 9),
    knob("Decay Time", 1200, 200, 60000),
    knob("Room Size", 100, 0.22, 500),
    knob("Stereo Image", 100, 0, 120),
    on("HiShelf On", true),
    knob("HiShelf Freq", 4500, 20, 16000),
    knob("HiShelf Gain", 0.7, 0.2, 1),
    knob("Reflect Level", 0, -30, 6),
    knob("Diffuse Level", 0, -30, 6),
    on("Freeze On", false),
    knob("Dry/Wet", 0.3, 0, 1),
  ],
  "Hybrid Reverb": () => [on("Device On", true), knob("Predelay", 10, 0.5, 250), knob("Decay", 2000, 200, 60000), knob("Size", 100, 0.22, 500), knob("Dry/Wet", 0.3, 0, 1)],
  "Auto Filter": () => [
    on("Device On", true),
    pick("Filter Type", 0, ["Lowpass", "Highpass", "Bandpass", "Notch"]),
    knob("Frequency", 2000, 20, 20000),
    knob("Resonance", 0.3, 0, 1.25),
  ],
  "EQ Three": () => [
    on("Device On", true),
    knob("GainLo", 0, -70, 6),
    knob("GainMid", 0, -70, 6),
    knob("GainHi", 0, -70, 6),
    knob("FreqLo", 250, 50, 5000),
    knob("FreqHi", 2500, 500, 15000),
    on("LowOn", true),
    on("MidOn", true),
    on("HighOn", true),
  ],
  "Channel EQ": () => [on("Device On", true), knob("Low Gain", 0, -15, 15), knob("Mid Gain", 0, -12, 12), knob("Mid Freq", 1500, 120, 7500), knob("High Gain", 0, -15, 15)],
  Compressor: () => [
    on("Device On", true),
    knob("Threshold", -18, -60, 0),
    knob("Ratio", 4, 1, 20),
    knob("Attack", 10, 0.01, 1000),
    knob("Release", 50, 1, 3000),
    knob("Knee", 6, 0, 18),
    knob("Output Gain", 0, -36, 36),
    knob("Dry/Wet", 1, 0, 1),
  ],
  "Glue Compressor": () => [on("Device On", true), knob("Threshold", -10, -40, 0), knob("Ratio", 4, 2, 10), knob("Makeup", 0, 0, 20), knob("Dry/Wet", 1, 0, 1)],
  Limiter: () => [on("Device On", true), knob("Gain", 0, -24, 24), knob("Ceiling", -0.3, -24, 0), knob("Release", 300, 1, 3000)],
  Saturator: () => [on("Device On", true), knob("Drive", 6, 0, 36), knob("Output", 0, -36, 0), knob("Dry/Wet", 1, 0, 1)],
  Overdrive: () => [on("Device On", true), knob("Drive", 0.5, 0, 1), knob("Dry/Wet", 1, 0, 1)],
  Pedal: () => [on("Device On", true), knob("Gain", 0.5, 0, 1), knob("Dry/Wet", 1, 0, 1)],
  Roar: () => [on("Device On", true), knob("Drive", 0.4, 0, 1), knob("Dry/Wet", 1, 0, 1)],
  "Drum Buss": () => [on("Device On", true), knob("Drive", 0.2, 0, 1), knob("Dry/Wet", 1, 0, 1)],
  Erosion: () => [on("Device On", true), knob("Amount", 0.2, 0, 1)],
  "Vinyl Distortion": () => [on("Device On", true), knob("Drive", 0.2, 0, 1)],
  Redux: () => [on("Device On", true), knob("Bit Depth", 8, 1, 16), knob("Dry/Wet", 1, 0, 1)],
  Utility: () => [
    on("Device On", true),
    knob("Gain", 0, -35, 35),
    knob("Stereo Width", 1, 0, 4),
    knob("Balance", 0, -1, 1),
    on("Mono", false),
    on("Left Inv", false),
    on("Right Inv", false),
    on("Mute", false),
  ],
  "Chorus-Ensemble": () => [on("Device On", true), knob("Rate", 0.8, 0.01, 10), knob("Amount", 0.4, 0, 1), knob("Feedback", 0, 0, 1), knob("Dry/Wet", 0.5, 0, 1)],
  "Phaser-Flanger": () => [
    on("Device On", true),
    pick("Mode", 0, ["Phaser", "Flanger", "Doubler"]),
    knob("Center Freq", 800, 50, 18000),
    knob("Rate", 0.5, 0.01, 10),
    knob("Amount", 0.5, 0, 1),
    knob("Feedback", 0.3, 0, 1),
    knob("Dry/Wet", 0.5, 0, 1),
  ],
  "Auto Pan": () => [on("Device On", true), knob("Amount", 0.5, 0, 1), on("Sync", false), knob("Frequency", 1, 0.01, 10), pick("Synced Rate", 6, RATES), pick("Waveform", 0, ["Sine", "Triangle", "Saw Down", "Random"])],
};

/** A fresh instance of Live's `name` with its stock parameters, or null for
 * a device this table doesn't know (an instrument, a MIDI effect, a device
 * the browser doesn't model). */
export function stockDevice(name: string): DeviceState | null {
  const make = STOCK[name];
  return make ? { name, params: make() } : null;
}
